import {Injectable} from '@angular/core';
import {ApiEndpointsService} from '@rsp/shared/services/rest/api-endpoints.service';
import {ApiHttpService, HttpResult} from '@rsp/shared/services/rest/api-http.service';
import {TenantsCommunityTotalDTO} from '@rsp/models/dto/tenants-community-totals.dto';
import {IntervalEnum} from '@rsp/models/enum/interval.enum';
import {TenantsCommunityDatasDTO} from '@rsp/models/dto/tenants-community-data.dto';
import {QueryStringParameters} from '@rsp/shared/util/query-string-parameters';
import {CustomersEnergyDatasDTO} from '@rsp/models/dto/customers-energy-data.dto';
import {CustomersEnergyUsagesDTO} from '@rsp/models/dto/customers-energy-usage.dto';
import {dateToZonedDateTime, getEndOfDay, getStartOfDay} from '@rsp/shared/util/util';
import {ProductionSiteDTO, TenantsDetailsDTO} from '@rsp/models/dto/tenants-details.dto';
import {UserInfoDTO} from '@rsp/models/dto/user-info.dto';
import {TenantDTO} from '@rsp/models/dto/tenant.dto';
import {DateRangesDTO} from '@rsp/models/dto/date-range.dto';
import {Observable} from 'rxjs';

@Injectable()
export class RspEndpointsService extends ApiEndpointsService {

  public static USER_INFO = 'userinfo';

  public static TENANTS_COMMUNITY_DATA = 'tenants-community-data';
  public static TENANTS_COMMUNITY_TOTAL = 'tenants-aggregated-community-balances';

  public static CUSTOMER_ENERGY_DATA = 'customers-energy-data';
  public static CUSTOMER_ENERGY_USAGE = 'customers-energy-usage';

  public static TENANTS_DATA_RANGES = 'tenants-data-ranges';
  public static CUSTOMER_DATA_RANGES = 'customers-data-ranges';

  public static TENANT_DETAILS = 'details';

  public static TENANTS_CUSTOMER_PRODUCTION_SITES = 'tenants-and-customers-production-sites';

  public static POST_FILE_UPLOAD = 'customers-production-site-image';

  public static DELETE_FILE = 'customers-production-site-image';

  public static GET_PRODUCTION_SITE_IMAGE = 'tenants-production-site-image';

  // Start GET Section

  constructor(private apiHttpService: ApiHttpService) {
    super();
  }

  public getTenantsCommunityTotals(): Observable<HttpResult<TenantsCommunityTotalDTO[]>> {
    const now = new Date();

    let yesterday: Date = new Date(now);

    yesterday.setDate(yesterday.getDate() - 1);
    yesterday = getEndOfDay(yesterday);

    let dayBeforeYesterday = new Date(yesterday);
    dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 1);
    dayBeforeYesterday = getStartOfDay(dayBeforeYesterday);


    const interval: IntervalEnum = IntervalEnum.DAY;

    return this.getWithDateRange<TenantsCommunityTotalDTO[]>(RspEndpointsService.TENANTS_COMMUNITY_TOTAL,
      dayBeforeYesterday, yesterday, interval);
  }

  public getCommunityUsage(start: Date, end: Date, interval: IntervalEnum): Observable<HttpResult<TenantsCommunityTotalDTO[]>> {
    return this.getWithDateRange<TenantsCommunityTotalDTO[]>(RspEndpointsService.TENANTS_COMMUNITY_TOTAL, start, end, interval);
  }

  public getCustomersEnergyDatas(start: Date, end: Date, interval: IntervalEnum): Observable<HttpResult<CustomersEnergyDatasDTO>> {
    return this.getWithDateRange<CustomersEnergyDatasDTO>(RspEndpointsService.CUSTOMER_ENERGY_DATA, start, end, interval);
  }

  public getCustomersEnergyUsages(start: Date, end: Date, interval: IntervalEnum): Observable<HttpResult<CustomersEnergyUsagesDTO>> {
    return this.getWithDateRange<CustomersEnergyUsagesDTO>(RspEndpointsService.CUSTOMER_ENERGY_USAGE, start, end, interval);
  }

  public getTenantsCommunityDatas(start: Date, end: Date, interval: IntervalEnum): Observable<HttpResult<TenantsCommunityDatasDTO>> {
    return this.getWithDateRange<TenantsCommunityDatasDTO>(RspEndpointsService.TENANTS_COMMUNITY_DATA, start, end, interval);
  }

  public getTenantCommunityProductionSites(): Observable<HttpResult<ProductionSiteDTO[]>> {
    return this.apiHttpService.get<ProductionSiteDTO[]>(this.createUrl(
      RspEndpointsService.TENANTS_CUSTOMER_PRODUCTION_SITES
    ), {withCredentials: true});
  }

  public getUserInfo(): Observable<HttpResult<UserInfoDTO>> {
    return this.apiHttpService.get<UserInfoDTO>(this.createUrl(
      RspEndpointsService.USER_INFO
    ), {withCredentials: true});
  }

  public getTenant(tenant: string): Observable<HttpResult<TenantDTO>> {
    return this.apiHttpService.get<TenantDTO>(this.createUrl(
      'tenant/' + tenant
    ), {withCredentials: true});
  }

  public getTenantDetails(tenant: string): Observable<HttpResult<TenantsDetailsDTO>> {
    return this.apiHttpService.get<TenantsDetailsDTO>(this.createUrl(
      'tenant/' + tenant + '/' + RspEndpointsService.TENANT_DETAILS
    ), {withCredentials: true});
  }

  public getCustomerDateRange(): Observable<HttpResult<DateRangesDTO>> {
    return this.apiHttpService.get<DateRangesDTO>(this.createUrl(
      RspEndpointsService.CUSTOMER_DATA_RANGES), {withCredentials: true});
  }

  public getCommunityDateRange(): Observable<HttpResult<DateRangesDTO>> {
    return this.apiHttpService.get<DateRangesDTO>(this.createUrl(
      RspEndpointsService.TENANTS_DATA_RANGES), {withCredentials: true});
  }

  // End GET Section

  // Start POST Section

  public postFileUpload(file: File, id: any): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.apiHttpService.post(this.createUrlWithQueryParameters(RspEndpointsService.POST_FILE_UPLOAD,
      (qs: QueryStringParameters) => {
        qs.push('productionSiteId', id);
      }), formData, {withCredentials: true});
  }

  // End POST Section

  // Start DELETE Section

  public deleteFile(fileId: number): Promise<any> {
    return this.apiHttpService.delete(this.createUrlWithQueryParameters(RspEndpointsService.DELETE_FILE, (qs) => {
      qs.push('productionSiteId', fileId);
    }), {withCredentials: true});
  }

  // End DELETE Section

  private getWithDateRange<T>(path: string, start: Date, end: Date, interval: IntervalEnum): Observable<HttpResult<T>> {
    return this.apiHttpService.get<T>(this.createUrlWithQueryParameters(
      path, (qs: QueryStringParameters) => {
        qs.push('start', dateToZonedDateTime(start));
        qs.push('end', dateToZonedDateTime(end));
        qs.push('interval', interval);
      }
    ), {withCredentials: true});
  }

}
